'use client';

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { AmenitiesService } from '@/hotdesk-ui/services/amenities.service';
import { RootState } from '../store';

export interface AmenityItem {
    id: number;
    name: string;
    url?: string;
}

interface AmenitiesState {
    /** Map of amenityId → AmenityItem for O(1) lookups */
    items: Record<number, AmenityItem>;
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

const initialState: AmenitiesState = {
    items: {},
    loading: false,
    loaded: false,
    error: null,
};

/** Fetch all amenities and store as a map */
export const fetchAmenities = createAsyncThunk(
    'amenities/fetchAmenities',
    async (_, { getState }) => {
        const state = getState() as RootState;
        const { defaultLanguage } = state.listing;
        const apiConfig = await new ApiConfigration().getApiConfig(defaultLanguage);
        const service = new AmenitiesService(apiConfig);
        const response = await service.getAmenities('', defaultLanguage);
        const results = (response as any).results || [];

        const items: Record<number, AmenityItem> = {};
        for (const amenity of results) {
            if (amenity.id) {
                items[amenity.id] = {
                    id: amenity.id,
                    name: amenity.name,
                    url: amenity.url ? String(amenity.url) : undefined,
                };
            }
        }
        return items;
    }
);

const amenitiesSlice = createSlice({
    name: 'amenities',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAmenities.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAmenities.fulfilled, (state, action: PayloadAction<Record<number, AmenityItem>>) => {
                state.items = action.payload;
                state.loading = false;
                state.loaded = true;
            })
            .addCase(fetchAmenities.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch amenities';
            });
    },
});

export default amenitiesSlice.reducer;
